import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from "recharts";

const SCENARIO_COLORS = { conservative: "#14B8A6", balanced: "#D4AF37", bold: "#A855F7" };

const fmtPayback = (m) => (m == null ? "Beyond 5 yrs" : `${Math.floor(m / 12)} yr ${m % 12} mo`);

export function BusinessCase() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business-cases").then((r) => { setData(r.data); setForm(r.data.settings); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/business-cases/settings", form);
      setData(d); setForm(d.settings);
      toast.success("Assumptions updated — recorded in audit trail");
    } catch (e) {
      toast.error("Assumptions out of allowed range");
    }
    setSaving(false);
  };

  if (!data) return null;
  const keys = Object.keys(data.cases);
  const chartData = [0, 1, 2, 3, 4].map((y) => {
    const row = { year: `Yr ${y + 1}` };
    keys.forEach((k) => { row[k] = data.cases[k].cumulative_net[y]; });
    return row;
  });

  const slider = (label, key, min, max, suffix) => (
    <div className="flex-1 min-w-[180px]">
      <div className="flex justify-between text-[11px] text-black/55 mb-1">
        <span>{label}</span><span className="font-mono font-medium text-black">{form[key]}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} value={form[key]} data-testid={`bc-${key}-slider`}
        onChange={(e) => setForm({ ...form, [key]: Number(e.target.value) })} className="w-full accent-[#D4AF37]" />
    </div>
  );

  return (
    <div className="mt-8" data-testid="business-case-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Scenario Business Case</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Costed Comparison & Payback ({data.currency}, {data.horizon_years}-year horizon)</h2>
        </div>
        <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 max-w-md">{data.evidence_note}</span>
      </div>

      <div className="bg-white border border-black/10 rounded-sm p-5 flex flex-wrap items-end gap-6 mb-5" data-testid="bc-assumptions-bar">
        <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 w-full -mb-2">Adjustable assumptions (audited)</p>
        {slider("Discount rate", "discount_rate", 0, 30, "%")}
        {slider("Benefit realisation", "realisation_pct", 40, 100, "%")}
        {slider("Cost contingency", "contingency_pct", 0, 50, "%")}
        <button onClick={save} disabled={saving} data-testid="bc-save-assumptions-btn"
          className="text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2.5 font-semibold hover:bg-black/85 transition-colors disabled:opacity-50">
          {saving ? "Recalculating…" : "Apply & recalculate"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {keys.map((k) => {
          const c = data.cases[k];
          return (
            <div key={k} data-testid={`bc-card-${k}`}
              className={`bg-white border rounded-sm p-5 ${c.approved ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-black/10"}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold tracking-tight" style={{ color: SCENARIO_COLORS[k] }}>{c.scenario_name}</h3>
                {c.approved && <span className="text-[10px] uppercase tracking-wider bg-[#D4AF37] text-black px-2 py-0.5 rounded-sm font-semibold">Approved</span>}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
                {[["Investment", `R${c.total_investment}m`], ["Benefits", `R${c.total_benefits}m`],
                  ["NPV", `R${c.npv}m`], ["ROI", `${c.roi_pct}%`]].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-black/50">{l}</p>
                    <p className="font-display text-xl font-black">{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-black/8">
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Payback</p>
                <p className="font-display text-2xl font-black" style={{ color: SCENARIO_COLORS[k] }} data-testid={`bc-payback-${k}`}>{fmtPayback(c.payback_months)}</p>
              </div>
              <div className="flex h-2.5 mt-3 rounded-sm overflow-hidden" title="Benefit mix">
                {Object.entries(c.benefit_breakdown).map(([bl, pct], i) => (
                  <div key={bl} title={`${bl} ${pct}%`} style={{ width: `${pct}%`, background: SCENARIO_COLORS[k], opacity: 1 - i * 0.3 }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 mt-1 text-[10px] text-black/55">
                {Object.entries(c.benefit_breakdown).map(([bl, pct]) => <span key={bl}>{bl} {pct}%</span>)}
              </div>
              <p className="text-[11px] text-black/55 mt-3">{c.basis}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {c.non_financial.map((n) => <span key={n} className="text-[10px] border border-black/12 rounded-full px-2 py-0.5 text-black/60">{n}</span>)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-black/10 rounded-sm p-5" data-testid="bc-cashflow-chart">
        <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Cumulative net cashflow ({data.currency}) — payback where a line crosses zero</p>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, name) => [`R${v}m`, data.cases[name]?.scenario_name || name]} />
            <Legend formatter={(v) => data.cases[v]?.scenario_name || v} />
            <ReferenceLine y={0} stroke="#0A0A0A" strokeDasharray="4 4" />
            {keys.map((k) => (
              <Line key={k} type="monotone" dataKey={k} stroke={SCENARIO_COLORS[k]} strokeWidth={2.5} dot={{ r: 3 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
